import BoardTool from '@/components/common/ed-board/ed-board-tools/board-tool.interface';
import WboDrawerTools from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/wbo-drawer-tools';

/********************************** WHITEBOPHIR ***************************************************
 * This class was taken and adapted for the EDUAC project from the WHITEBOPHIR project            *
 * https://github.com/lovasoa/whitebophir, keeping the same distribution and license conditions.  *
 **************************************************************************************************
 */

export default class EdWboDrawerText {
    private board;
    private readonly input;
    private wboDrawerTools: WboDrawerTools;
    private active = false;

    private curText: any = {
        x          : 0,
        y          : 0,
        size       : 36,
        rawSize    : 16,
        oldSize    : 0,
        opacity    : 1,
        color      : '#000',
        id         : 0,
        sentText   : '',
        lastSending: 0
    };

    private onStart: Function = () => {
        this.curText.oldSize = this.wboDrawerTools.getSize();
        this.wboDrawerTools.setSize(this.curText.rawSize);
    }

    private clickHandler: Function = (x, y, evt, isTouchEvent) => {
        if (evt.target === this.input) {
            return;
        }

        if (evt.target.tagName === 'text') {
            this.editOldText(evt.target);
            evt.preventDefault();
            return;
        }

        this.curText.rawSize = this.wboDrawerTools.getSize();
        this.curText.size = this.curText.rawSize * 1.5 + 12;
        this.curText.opacity = this.wboDrawerTools.getOpacity();
        this.curText.color = this.wboDrawerTools.getColor();
        this.curText.x = x;
        this.curText.y = y + this.curText.size / 2;

        this.stopEdit();
        this.startEdit();
        evt.preventDefault();
    }

    private textChangeHandler: Function = (evt) => {
        if (evt.which === 13) { // enter
            this.curText.y += 1.5 * this.curText.size;
            this.stopEdit();
            this.startEdit();
        } else if (evt.which === 27) { // escape
            this.stopEdit();
        }
        if (performance.now() - this.curText.lastSending > 100) {
            if (this.curText.sentText !== this.input.value) {
                //If the user clicked where there was no text, then create a new text field
                if (this.curText.id === 0) {
                    this.curText.id = this.wboDrawerTools.generateUID('t'); //'t' for text
                    this.wboDrawerTools.drawAndSend({
                        type   : 'new',
                        id     : this.curText.id,
                        color  : this.curText.color,
                        size   : this.curText.size,
                        opacity: this.curText.opacity,
                        x      : this.curText.x,
                        y      : this.curText.y
                    })
                }
                this.wboDrawerTools.drawAndSend({
                    type: 'update',
                    id  : this.curText.id,
                    txt : this.input.value.slice(0, 280)
                });
                this.curText.sentText = this.input.value;
                this.curText.lastSending = performance.now();
            }
        } else {
            clearTimeout(this.curText.timeout);
            this.curText.timeout = setTimeout(this.textChangeHandler, 500, evt);
        }
    }

    private draw: Function = (data, isLocal) => {
        this.wboDrawerTools.drawingEvent = true;
        switch (data.type) {
            case 'new':
                this.createTextField(data);
                break;
            case 'update': {
                let textField = document.getElementById(data.id);
                if (textField === null) {
                    return false;
                }
                this.updateText(textField, data);
                break;
            }
            default:
                break;
        }
        return true;
    }

    private stopEdit: Function = () => {
        try {
            this.input.blur();
        } catch (e) { /* Internet Explorer */
        }
        this.active = false;
        this.blur();
        this.curText.id = 0;
        this.curText.sentText = '';
        this.input.value = '';
        this.input.removeEventListener('keyup', this.textChangeHandler);
    }

    private onQuit: Function = () => {
        this.stopEdit();
        this.wboDrawerTools.setSize(this.curText.oldSize);
    }

    private textTool: BoardTool = { //The new tool
        name       : 'Text',
        shortcut   : 't',
        listeners  : {
            press: this.clickHandler
        },
        onstart    : this.onStart,
        onquit     : this.onQuit,
        draw       : this.draw,
        icon       : '/assets/tools/text/icon.svg',
        mouseCursor: 'text'
    };

    constructor(tools: WboDrawerTools) {
        this.wboDrawerTools = tools;
        this.board = tools.board;
        this.input = document.createElement('input');
        this.input.type = 'text';
        this.input.classList.add('textToolInput');
        this.input.setAttribute('autocomplete', 'off');
        tools.add(this.textTool);
    }

    private editOldText(elem): void {
        this.curText.id = elem.id;
        let r = elem.getBoundingClientRect();

        let x = parseInt(elem.getAttribute('x'));
        let y = parseInt(elem.getAttribute('y'));

        this.curText.x = x;
        this.curText.y = y;
        this.curText.sentText = elem.textContent;
        this.curText.size = parseInt(elem.getAttribute('font-size'));
        this.curText.opacity = parseFloat(elem.getAttribute('opacity'));
        this.curText.color = elem.getAttribute('fill');
        this.startEdit();
        this.input.value = elem.textContent;
    }

    private startEdit(): void {
        let pageContainerRect = this.wboDrawerTools.pageContainer.getBoundingClientRect();
        let boardRect = this.board.getBoundingClientRect();

        this.active = true;
        if (!this.input.parentNode) this.board.appendChild(this.input);
        this.input.value = '';
        let left = this.curText.x - this.wboDrawerTools.pageContainer.scrollLeft + 'px';
        let clientW = Math.max(this.wboDrawerTools.pageContainer.clientWidth, window.innerWidth || 0);
        let x = this.curText.x * this.wboDrawerTools.scale - this.wboDrawerTools.pageContainer.scrollLeft;
        if (x + 250 > clientW) {
            x = Math.max(60, clientW - 260)
        }

        let yScaled = this.curText.y * this.wboDrawerTools.scale;
        this.input.style.left = x + pageContainerRect.left + 'px';
        this.input.style.top = yScaled + 20 + boardRect.top + 'px';
        this.input.focus();
        this.input.addEventListener('keyup', this.textChangeHandler);
        this.input.addEventListener('blur', this.textChangeHandler);
        this.input.addEventListener('blur', blur);
    }

    private blur(): void {
        if (this.active) {
            return;
        }
        this.input.style.top = '-1000px';
    }

    private updateText(textField, data): void {
        textField.textContent = data.txt;
        this.wboDrawerTools.addAnnotationOwners(textField, data);
    }

    private createTextField(fieldData) {
        let elem = this.wboDrawerTools.createSVGElement('text');
        elem.id = fieldData.id;
        elem.setAttribute('x', fieldData.x);
        elem.setAttribute('y', fieldData.y);
        elem.setAttribute('font-size', fieldData.size);
        elem.setAttribute('fill', fieldData.color);
        elem.setAttribute('opacity', String(Math.max(0.1, Math.min(1, fieldData.opacity)) || 1));
        fieldData.transform && elem.setAttribute('transform', fieldData.transform);
        if (fieldData.txt) {
            elem.textContent = fieldData.txt;
        }
        this.wboDrawerTools.addAnnotationOwners(elem, fieldData);
        this.wboDrawerTools.drawingArea.appendChild(elem);
        return elem;
    }

}